/**
 * Encrypted backup container.
 *
 * The file is self-describing and self-contained: a plaintext JSON header with
 * the KDF parameters, then one AES-GCM sealed blob holding everything. It can
 * be restored on a fresh device with nothing but the passphrase — no account,
 * no key material, no server.
 *
 * The passphrase is asked for separately from the login password rather than
 * reusing the account key. A backup that can only be opened by the device that
 * wrote it is not a backup; it is a second copy of the same single point of
 * failure.
 */

import {
  deriveKeyMaterial,
  fromBase64,
  importAesKey,
  openJson,
  randomBytes,
  sealJson,
  sha256Hex,
  toBase64,
  type Sealed,
} from '@/crypto/primitives';
import { verifyChain, type ChainVerification } from '@/db/audit';
import { accountsDb, db } from '@/db/schema';
import { nowIso } from '@/domain/dates';
import type {
  Address,
  AppSettings,
  AuditEntry,
  CompanySettings,
  Invoice,
  Order,
  Passenger,
  Shift,
  Trip,
  Vehicle,
} from '@/domain/types';
import {
  addressRepo,
  getAppSettings,
  getCompanySettings,
  invoiceRepo,
  orderRepo,
  passengerRepo,
  shiftRepo,
  tripRepo,
  vehicleRepo,
} from '@/db/repositories';
import { readAuditEntry } from '@/db/audit';

export const BACKUP_MAGIC = 'fahrtenbuch-pro-backup';
export const BACKUP_FORMAT_VERSION = 1;

export interface BackupHeader {
  magic: typeof BACKUP_MAGIC;
  formatVersion: number;
  createdAt: string;
  /** Free-text so a human can tell two files apart without opening them. */
  label: string;
  kdf: {
    algorithm: 'argon2id';
    salt: string;
    memoryKib: number;
    iterations: number;
    parallelism: number;
  };
  /** Counts, in clear, so a restore can be previewed before decrypting. */
  summary: {
    trips: number;
    shifts: number;
    orders: number;
    invoices: number;
    passengers: number;
    addresses: number;
    vehicles: number;
    auditEntries: number;
  };
  /** Chain head at the time of the backup, for after-the-fact comparison. */
  chainHeadHash: string;
  chainOk: boolean;
}

export interface BackupPayload {
  company: CompanySettings;
  app: AppSettings;
  trips: Trip[];
  shifts: Shift[];
  orders: Order[];
  invoices: Invoice[];
  passengers: Passenger[];
  addresses: Address[];
  vehicles: Vehicle[];
  auditEntries: AuditEntry[];
  /** Account records, so a restore reproduces the login too. */
  accounts: unknown[];
}

export interface BackupFile {
  header: BackupHeader;
  body: Sealed;
  /** SHA-256 over the canonical header plus the ciphertext. */
  checksum: string;
}

async function checksumOf(header: BackupHeader, body: Sealed): Promise<string> {
  return sha256Hex(`${JSON.stringify(header)}|${body.iv}|${body.ct}`);
}

export interface CreateBackupParams {
  dataKey: CryptoKey;
  passphrase: string;
  label?: string;
  /** Whose backup this is. Only their login is written into the file. */
  accountId: string;
}

export async function collectBackupPayload(
  dataKey: CryptoKey,
  accountId: string,
): Promise<{
  payload: BackupPayload;
  chain: ChainVerification;
}> {
  const [company, app, trips, shifts, orders, invoices, passengers, addresses, vehicles, account] =
    await Promise.all([
      getCompanySettings(dataKey),
      getAppSettings(dataKey),
      tripRepo.all(dataKey),
      shiftRepo.all(dataKey),
      orderRepo.all(dataKey),
      invoiceRepo.all(dataKey),
      passengerRepo.all(dataKey),
      addressRepo.all(dataKey),
      vehicleRepo.all(dataKey),
      // The registry is shared by every driver on the device, so this is one
      // row and not the table: another driver's wrapped key has no business in
      // a backup file this one carries around.
      accountsDb.accounts.get(accountId),
    ]);
  const accounts = account ? [account] : [];

  const auditEnvelopes = await db.auditLog.orderBy('seq').toArray();
  const auditEntries = await Promise.all(
    auditEnvelopes.map((envelope) => readAuditEntry(dataKey, envelope)),
  );

  const chain = await verifyChain(dataKey);

  return {
    payload: {
      company,
      app,
      trips,
      shifts,
      orders,
      invoices,
      passengers,
      addresses,
      vehicles,
      auditEntries,
      accounts,
    },
    chain,
  };
}

export async function createBackupFile(params: CreateBackupParams): Promise<{
  blob: Blob;
  header: BackupHeader;
}> {
  const { payload, chain } = await collectBackupPayload(params.dataKey, params.accountId);

  const salt = randomBytes(16);
  const keyMaterial = await deriveKeyMaterial(params.passphrase, salt);
  const key = await importAesKey(keyMaterial);
  keyMaterial.fill(0);

  const header: BackupHeader = {
    magic: BACKUP_MAGIC,
    formatVersion: BACKUP_FORMAT_VERSION,
    createdAt: nowIso(),
    label: params.label ?? '',
    kdf: {
      algorithm: 'argon2id',
      salt: toBase64(salt),
      memoryKib: 65_536,
      iterations: 3,
      parallelism: 1,
    },
    summary: {
      trips: payload.trips.length,
      shifts: payload.shifts.length,
      orders: payload.orders.length,
      invoices: payload.invoices.length,
      passengers: payload.passengers.length,
      addresses: payload.addresses.length,
      vehicles: payload.vehicles.length,
      auditEntries: payload.auditEntries.length,
    },
    chainHeadHash: chain.headHash,
    chainOk: chain.ok,
  };

  const body = await sealJson(key, payload);
  const file: BackupFile = { header, body, checksum: await checksumOf(header, body) };

  const blob = new Blob([JSON.stringify(file)], { type: 'application/json' });
  return { blob, header };
}

export class BackupFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BackupFormatError';
  }
}

export class BackupPassphraseError extends Error {
  constructor() {
    super('Die Passphrase passt nicht zu dieser Sicherungsdatei.');
    this.name = 'BackupPassphraseError';
  }
}

export async function parseBackupFile(text: string): Promise<BackupFile> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new BackupFormatError('Die Datei ist keine gültige Sicherungsdatei.');
  }

  const file = parsed as Partial<BackupFile>;
  if (file.header?.magic !== BACKUP_MAGIC) {
    throw new BackupFormatError('Die Datei stammt nicht aus Fahrtenbuch Pro.');
  }
  if (file.header.formatVersion > BACKUP_FORMAT_VERSION) {
    throw new BackupFormatError(
      `Die Sicherung wurde mit einer neueren Version erstellt (Format ${file.header.formatVersion}).`,
    );
  }
  if (!file.body?.iv || !file.body.ct) {
    throw new BackupFormatError('Die Sicherungsdatei ist unvollständig.');
  }

  const expected = await checksumOf(file.header, file.body);
  if (file.checksum && file.checksum !== expected) {
    throw new BackupFormatError(
      'Die Prüfsumme stimmt nicht. Die Datei wurde beschädigt oder verändert.',
    );
  }

  return file as BackupFile;
}

export async function openBackupPayload(
  file: BackupFile,
  passphrase: string,
): Promise<BackupPayload> {
  const keyMaterial = await deriveKeyMaterial(passphrase, fromBase64(file.header.kdf.salt));
  const key = await importAesKey(keyMaterial);
  keyMaterial.fill(0);

  try {
    return await openJson<BackupPayload>(key, file.body);
  } catch {
    throw new BackupPassphraseError();
  }
}

export interface RestoreResult {
  restored: BackupHeader['summary'];
  chainAfterRestore: ChainVerification;
}

/**
 * Replace the local database with the backup's contents.
 *
 * Destructive by design: a partial merge would produce a hash chain that is
 * neither the backup's nor the device's, and a chain that cannot be verified
 * is worth nothing. The caller must confirm.
 */
export async function restoreBackup(
  payload: BackupPayload,
  dataKey: CryptoKey,
): Promise<RestoreResult> {
  await db.transaction(
    'rw',
    [
      db.trips,
      db.shifts,
      db.orders,
      db.invoices,
      db.passengers,
      db.addresses,
      db.vehicles,
      db.settings,
      db.auditLog,
    ],
    async () => {
      await Promise.all([
        db.trips.clear(),
        db.shifts.clear(),
        db.orders.clear(),
        db.invoices.clear(),
        db.passengers.clear(),
        db.addresses.clear(),
        db.vehicles.clear(),
        db.settings.clear(),
        db.auditLog.clear(),
      ]);
    },
  );

  await Promise.all([
    tripRepo.restoreRaw(dataKey, payload.trips),
    shiftRepo.restoreRaw(dataKey, payload.shifts),
    orderRepo.restoreRaw(dataKey, payload.orders),
    invoiceRepo.restoreRaw(dataKey, payload.invoices),
    passengerRepo.restoreRaw(dataKey, payload.passengers),
    addressRepo.restoreRaw(dataKey, payload.addresses),
    vehicleRepo.restoreRaw(dataKey, payload.vehicles),
  ]);

  // Audit entries are re-sealed under the current data key but keep their
  // original sequence numbers, hashes and links, so the restored chain
  // verifies exactly as it did before.
  const auditEnvelopes = await Promise.all(
    payload.auditEntries.map(async (entry) => {
      const sealed = await sealJson(dataKey, entry);
      return {
        id: entry.id,
        bucket: entry.at.slice(0, 7),
        iv: sealed.iv,
        ct: sealed.ct,
        pv: 1,
        seq: entry.seq,
        prevHash: entry.prevHash,
        hash: entry.hash,
        at: entry.at,
        entityType: entry.entityType,
        entityId: entry.entityId,
      };
    }),
  );
  await db.auditLog.bulkPut(auditEnvelopes);

  const [companySealed, appSealed] = await Promise.all([
    sealJson(dataKey, payload.company),
    sealJson(dataKey, payload.app),
  ]);
  await db.settings.bulkPut([
    { id: 'company', bucket: 'static', iv: companySealed.iv, ct: companySealed.ct, pv: 1 },
    { id: 'app', bucket: 'static', iv: appSealed.iv, ct: appSealed.ct, pv: 1 },
  ]);

  return {
    restored: {
      trips: payload.trips.length,
      shifts: payload.shifts.length,
      orders: payload.orders.length,
      invoices: payload.invoices.length,
      passengers: payload.passengers.length,
      addresses: payload.addresses.length,
      vehicles: payload.vehicles.length,
      auditEntries: payload.auditEntries.length,
    },
    chainAfterRestore: await verifyChain(dataKey),
  };
}

/** Suggested filename: sortable, and obvious about what it holds. */
export function backupFileName(createdAt: string): string {
  return `fahrtenbuch-sicherung-${createdAt.slice(0, 10)}-${createdAt.slice(11, 16).replace(':', '')}.fbbak`;
}
